import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, BookOpen, Users, Calendar, Plus, X, Trash2,
  Edit3, LogOut, UserPlus, Bookmark,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const DEFAULT_CADENCES = [
  { id: "weekly", label: "Weekly" },
  { id: "fortnightly", label: "Fortnightly" },
  { id: "monthly", label: "Monthly" },
  { id: "self-paced", label: "Self-paced" },
];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch (e) { return iso; }
}

// ──────────────────────────────────────────────────────────────────────────────
// Home — list circles
// ──────────────────────────────────────────────────────────────────────────────
export function ReadingCirclesHome() {
  const [circles, setCircles] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [cadences, setCadences] = useState(DEFAULT_CADENCES);
  const { user } = useAuth();

  const load = async () => {
    try {
      const r = await api.get("/reading-circles");
      setCircles(r.data.circles || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    api.get("/reading-circles/cadences").then((r) => setCadences(r.data.cadences || DEFAULT_CADENCES)).catch(() => {});
  }, []);

  return (
    <div className="px-4 sm:px-8 lg:px-10 py-8 max-w-[1400px] mx-auto" data-testid="reading-circles-home">
      <Link to="/platform" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-5">
        <ArrowLeft size={14} /> Back to dashboard
      </Link>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-saffron">Reading circles</p>
          <h1 className="font-display text-4xl text-navy">Read together</h1>
          <p className="mt-2 text-navy/65 max-w-2xl">
            Small groups reading a book or sutta together at a steady pace.
            Join an existing circle or start your own.
          </p>
        </div>
        {user && (
          <button
            onClick={() => setShowNew(true)}
            data-testid="rc-new-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-navy text-pearl text-sm rounded-md hover:bg-navy-light"
          >
            <Plus size={16} /> Start a circle
          </button>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {circles.length === 0 ? (
          <div className="md:col-span-2 lg:col-span-3 bg-white border border-navy/10 rounded-xl p-12 text-center text-navy/55">
            <BookOpen size={32} className="mx-auto mb-3 text-saffron" />
            No circles yet. {user ? "Start the first one." : "Login to start the first one."}
          </div>
        ) : (
          circles.map((c) => (
            <Link
              key={c.circle_id}
              to={`/platform/reading-circles/${c.circle_id}`}
              data-testid={`rc-card-${c.circle_id}`}
              className="bg-white border border-navy/10 rounded-xl p-5 hover:shadow-md transition-shadow"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-saffron">{c.cadence}</div>
              <h2 className="font-display text-xl text-navy mt-1.5 leading-tight">{c.title}</h2>
              <div className="text-sm text-navy/70 mt-1.5 flex items-center gap-1.5">
                <Bookmark size={13} /> {c.book}{c.author ? <span className="text-navy/40"> · {c.author}</span> : null}
              </div>
              {c.current_chapter && (
                <div className="mt-2 text-xs text-navy/55">Currently reading: <span className="text-navy/85 font-medium">{c.current_chapter}</span></div>
              )}
              {c.description && <p className="mt-3 text-sm text-navy/65 line-clamp-3">{c.description}</p>}
              <div className="mt-4 flex items-center justify-between text-xs text-navy/55">
                <span className="flex items-center gap-1"><Users size={12} /> {c.member_count} / {c.max_members}</span>
                {c.next_meet_at && (
                  <span className="flex items-center gap-1"><Calendar size={12} /> {fmtDate(c.next_meet_at)}</span>
                )}
              </div>
            </Link>
          ))
        )}
      </div>

      {showNew && (
        <NewCircleModal
          cadences={cadences}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}
    </div>
  );
}

function NewCircleModal({ cadences, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", book: "", author: "", cadence: "weekly",
    description: "", current_chapter: "", next_meet_at: "", max_members: 25,
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.next_meet_at) delete payload.next_meet_at;
      const r = await api.post("/reading-circles", payload);
      toast.success("Reading circle created");
      onCreated(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create");
    } finally { setBusy(false); }
  };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="rc-new-modal">
      <form onSubmit={submit} className="bg-pearl rounded-xl shadow-2xl w-full max-w-xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-5 py-3 border-b border-navy/10 flex items-center justify-between">
          <div className="font-display text-lg text-navy">Start a reading circle</div>
          <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <Field label="Circle title">
            <input value={form.title} onChange={(e) => upd("title", e.target.value)} required maxLength={140} className="form-input w-full" data-testid="rc-new-title" autoFocus />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Book / Sutta">
              <input value={form.book} onChange={(e) => upd("book", e.target.value)} required maxLength={140} className="form-input w-full" data-testid="rc-new-book" />
            </Field>
            <Field label="Author / Translator">
              <input value={form.author} onChange={(e) => upd("author", e.target.value)} maxLength={120} className="form-input w-full" />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Cadence">
              <select value={form.cadence} onChange={(e) => upd("cadence", e.target.value)} className="form-input w-full" data-testid="rc-new-cadence">
                {cadences.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Max members">
              <input type="number" min={2} max={100} value={form.max_members} onChange={(e) => upd("max_members", parseInt(e.target.value || "25", 10))} className="form-input w-full" />
            </Field>
          </div>
          <Field label="Currently reading (chapter / sutta)">
            <input value={form.current_chapter} onChange={(e) => upd("current_chapter", e.target.value)} maxLength={140} className="form-input w-full" placeholder="e.g. Chapter I, Uraga Sutta" />
          </Field>
          <Field label="Next meet (optional)">
            <input type="datetime-local" value={form.next_meet_at} onChange={(e) => upd("next_meet_at", e.target.value)} className="form-input w-full" />
          </Field>
          <Field label="Description">
            <textarea value={form.description} onChange={(e) => upd("description", e.target.value)} rows={4} maxLength={1500} className="form-input w-full font-serif" placeholder="What will the circle do? When and how do you meet?" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-navy/10">
          <button type="button" onClick={onClose} className="px-4 py-2 text-navy/60 hover:text-navy text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="px-5 py-2 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light disabled:opacity-40" data-testid="rc-new-submit">
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">{label}</span>
      {children}
    </label>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Detail
// ──────────────────────────────────────────────────────────────────────────────
export function ReadingCircleDetail() {
  const { circleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [circle, setCircle] = useState(null);
  const [editing, setEditing] = useState(false);
  const [chapterDraft, setChapterDraft] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/reading-circles/${circleId}`);
      setCircle(r.data);
      setChapterDraft(r.data.current_chapter || "");
    } catch (e) {
      toast.error("Circle not found");
      navigate("/platform/reading-circles");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [circleId]);

  if (!circle) return <div className="p-12 text-center text-navy/60">Loading…</div>;

  const uid = user?.id || user?.user_id;
  const isMember = (circle.members || []).some((m) => m.user_id === uid);
  const isLeader = circle.leader?.user_id === uid;
  const isAdmin = user?.role === "admin";

  const join = async () => {
    try {
      const r = await api.post(`/reading-circles/${circleId}/join`);
      setCircle(r.data);
      toast.success("Welcome to the circle");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not join");
    }
  };
  const leave = async () => {
    if (!window.confirm("Leave this circle?")) return;
    try {
      const r = await api.post(`/reading-circles/${circleId}/leave`);
      setCircle(r.data);
      toast.success("You've left the circle");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not leave");
    }
  };
  const updateChapter = async () => {
    try {
      const r = await api.put(`/reading-circles/${circleId}`, { current_chapter: chapterDraft });
      setCircle(r.data);
      setEditing(false);
      toast.success("Chapter updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    }
  };
  const remove = async () => {
    if (!window.confirm("Delete this whole circle? This cannot be undone.")) return;
    try {
      await api.delete(`/reading-circles/${circleId}`);
      toast.success("Circle deleted");
      navigate("/platform/reading-circles");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete");
    }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-10 py-8 max-w-4xl mx-auto" data-testid="rc-detail">
      <Link to="/platform/reading-circles" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-5">
        <ArrowLeft size={14} /> All circles
      </Link>
      <article className="bg-white border border-navy/10 rounded-xl p-6 sm:p-8 shadow-sm">
        <div className="text-[10px] uppercase tracking-[0.25em] text-saffron">{circle.cadence} reading circle</div>
        <h1 className="font-display text-3xl sm:text-4xl text-navy mt-2 leading-tight">{circle.title}</h1>
        <div className="mt-2 text-navy/70 text-sm flex items-center gap-1.5">
          <BookOpen size={14} /> {circle.book}{circle.author ? <span className="text-navy/45"> · {circle.author}</span> : null}
        </div>

        <div className="mt-5 bg-pearl border border-navy/10 rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="rc-chapter-row">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-navy/55">Currently reading</div>
            {editing ? (
              <input value={chapterDraft} onChange={(e) => setChapterDraft(e.target.value)} maxLength={140} className="form-input w-full mt-1" autoFocus data-testid="rc-chapter-input" />
            ) : (
              <div className="font-display text-lg text-navy mt-0.5">{circle.current_chapter || <span className="text-navy/40 italic">Not set yet</span>}</div>
            )}
          </div>
          {(isLeader || isAdmin) && !editing && (
            <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-navy/15 hover:border-saffron hover:text-saffron text-xs rounded-md" data-testid="rc-chapter-edit">
              <Edit3 size={12} /> Edit
            </button>
          )}
          {editing && (
            <div className="flex gap-2">
              <button onClick={() => { setEditing(false); setChapterDraft(circle.current_chapter || ""); }} className="px-3 py-1.5 text-navy/60 hover:text-navy text-xs">Cancel</button>
              <button onClick={updateChapter} className="px-3 py-1.5 bg-navy text-pearl rounded-md text-xs" data-testid="rc-chapter-save">Save</button>
            </div>
          )}
        </div>

        {circle.description && (
          <p className="mt-5 text-navy/80 font-serif leading-relaxed whitespace-pre-wrap">{circle.description}</p>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {!isMember && user && (
            <button onClick={join} className="inline-flex items-center gap-1.5 px-4 py-2 bg-saffron text-navy rounded-md text-sm font-medium hover:bg-saffron-light" data-testid="rc-join-button">
              <UserPlus size={14} /> Join the circle ({circle.member_count} / {circle.max_members})
            </button>
          )}
          {isMember && !isLeader && (
            <button onClick={leave} className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white border border-navy/15 hover:border-red-500 hover:text-red-700 text-sm rounded-md" data-testid="rc-leave-button">
              <LogOut size={14} /> Leave
            </button>
          )}
          {(isLeader || isAdmin) && (
            <button onClick={remove} className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white border border-red-300 text-red-700 hover:bg-red-50 text-sm rounded-md" data-testid="rc-delete-button">
              <Trash2 size={14} /> Delete circle
            </button>
          )}
          {!user && (
            <Link to="/login" className="text-saffron text-sm hover:text-saffron-light">Login to join this circle →</Link>
          )}
        </div>
      </article>

      <section className="mt-7">
        <h2 className="font-display text-xl text-navy mb-3">Members · {circle.member_count}</h2>
        <div className="bg-white border border-navy/10 rounded-xl divide-y divide-navy/5 overflow-hidden">
          {(circle.members || []).map((m) => (
            <div key={m.user_id} className="px-4 py-3 flex items-center gap-3" data-testid={`rc-member-${m.user_id}`}>
              <div className="w-9 h-9 rounded-full bg-saffron/15 text-saffron font-display flex items-center justify-center flex-shrink-0">
                {(m.name || "M").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                {m.membership_id ? (
                  <Link to={`/platform/profile/${m.membership_id}`} className="text-sm font-medium text-navy hover:text-saffron truncate">{m.name}</Link>
                ) : (
                  <span className="text-sm font-medium text-navy">{m.name}</span>
                )}
                {circle.leader?.user_id === m.user_id && (
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-saffron">Leader</span>
                )}
              </div>
              <span className="text-xs text-navy/45">Joined {fmtDate(m.joined_at)}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
